import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { FeatureService } from '@services/features/feature.service';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { OrganizationAction, OrganizationState } from '@state/organization.state';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { SolutionAction } from '@state/solution.state';
import { UserAction, UserState } from '@state/user.state';
import { ConfigService } from 'app/app-config.service';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { AuthenticationService, CognitoAuthenticationService } from 'app/auth/service';
import { DialogService } from 'primeng/dynamicdialog';
import {
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    of,
    shareReplay,
    startWith,
    switchMap,
} from 'rxjs';
import { buildNavigation } from './navigation/build-navigation';
import { Navigation } from './navigation/nav-item.model';
import { NeedHelpModalComponent } from './need-help-modal/need-help-modal.component';

/** The logo of the build (each client build ships its own file at this path). */
const DEFAULT_LOGO = 'assets/images/logo/logo-h.webp';
/** D-Carbonize's free-trial pricing plan. */
const FREE_TRIAL_PLAN_ID = 'bc1b9506-260b-4b3c-b680-126505a307cb';
const DAY_MS = 24 * 60 * 60 * 1000;

export interface ShellBranding {
    logo: string;
    organizationName: string;
    canSwitchOrganization: boolean;
}

export interface ShellAccount {
    name: string;
    email: string;
}

/**
 * Everything the app frame shows, derived from the store, and what its controls do. Provided by
 * the layout component, which hands the values to its presentational children.
 */
@Injectable()
export class ShellFacade {
    readonly currentUrl$: Observable<string> = this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        startWith(this.router.url),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly loading$: Observable<boolean> = this.featureService.UserReadiness.pipe(map((ready) => !ready));

    readonly languages: string[] = this.translateService.langs;

    readonly currentLanguage$: Observable<string> = this.translateService.onLangChange.pipe(
        map((event) => event.lang),
        startWith(this.translateService.currentLang),
    );

    /** Client builds credit D-Carbonize at the foot of the sidebar. */
    readonly showPoweredBy = this.configService.getClient.name.toLowerCase() !== 'dcarbonize';

    private readonly claims$ = this.store.select(UserState.getCurrentUserClaims);
    private readonly pricingPlan$ = this.store.select(OrganizationState.getOrganizationPricingPlan);

    private readonly hasSeveralOrganizations$: Observable<boolean> = this.featureService.feature$.pipe(
        map((feature) => feature.organization?.uid),
        distinctUntilChanged(),
        switchMap((organizationId) =>
            organizationId
                ? this.userHttpService.getAvailableOrganization().pipe(
                      map((organizations) => organizations.length > 1),
                      catchError(() => of(false)),
                  )
                : of(false),
        ),
        startWith(false),
    );

    readonly branding$: Observable<ShellBranding> = combineLatest([this.claims$, this.hasSeveralOrganizations$]).pipe(
        map(([claims, hasSeveralOrganizations]) => ({
            logo: claims?.organization?.avatar || DEFAULT_LOGO,
            organizationName: claims?.organization?.name ?? '',
            canSwitchOrganization: hasSeveralOrganizations && this.featureService.hasUiFeatures(['organization'], true),
        })),
    );

    readonly navigation$: Observable<Navigation> = combineLatest([
        this.featureService.feature$,
        this.pricingPlan$,
        this.store.select(UserState.getUserStatus),
        this.currentLanguage$,
    ]).pipe(map(([, pricingPlan]) => this.buildNavigation(pricingPlan)));

    readonly account$: Observable<ShellAccount> = this.store.select(UserState.getCurrentUser).pipe(
        map((user) => ({
            name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
            email: user?.email ?? '',
        })),
    );

    /** Days left in the free trial, or null when the organisation is not on it. */
    readonly freeTrialDaysLeft$: Observable<number | null> = this.pricingPlan$.pipe(
        map((plan) =>
            plan?.pricingPlanId === FREE_TRIAL_PLAN_ID && this.configService.getClient.name === 'Dcarbonize'
                ? Math.floor((new Date(plan.validUntil).getTime() - Date.now()) / DAY_MS) + 1
                : null,
        ),
    );

    /** The organisation a platform admin is controlling, or null outside impersonation. */
    readonly controlledOrganization$: Observable<string | null> = combineLatest([
        this.featureService.feature$,
        this.claims$,
    ]).pipe(map(([feature, claims]) => (feature.hasSudoFeature() ? (claims?.organization?.name ?? '') : null)));

    constructor(
        private readonly router: Router,
        private readonly store: Store,
        private readonly featureService: FeatureService,
        private readonly translateService: CustomTranslateService,
        private readonly configService: ConfigService,
        private readonly userHttpService: UserHttpService,
        private readonly authenticationService: AuthenticationService,
        private readonly cognitoAuthenticationService: CognitoAuthenticationService,
        private readonly storageService: StorageService,
        private readonly adminDataSync: AdminDataSyncService,
        private readonly dialogService: DialogService,
    ) {}

    changeLanguage(language: string): void {
        if (!this.cognitoAuthenticationService.authenticatedUser) {
            this.translateService.use(language);
            this.storageService.setGlobalItem(LocalStorageKeys.localeUser, language);
            return;
        }
        this.cognitoAuthenticationService.updateUserLocale(language).subscribe((saved) => {
            if (saved) {
                this.translateService.use(language);
            }
        });
    }

    logout(): void {
        this.authenticationService.logout().subscribe();
    }

    /** Opens an e-mail to D-Carbonize asking to upgrade from the free trial. */
    contactSales(): void {
        const user = this.store.selectSnapshot(UserState.getCurrentUser);
        const plan = this.store.selectSnapshot(OrganizationState.getOrganizationPricingPlan);
        const name = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Undefined';
        const recipient = this.configService.getClient.support?.mailDcarbonize ?? '';

        forkJoin({
            subject: this.translateService.get('menu.pricingPlan.mail.subject', { name }),
            body: this.translateService.get('menu.pricingPlan.mail.body', {
                name,
                organisationId: plan?.organizationId ?? '',
                clientName: name,
            }),
        }).subscribe(({ subject, body }) => {
            const mail = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.open(mail, 'emailWindow');
        });
    }

    /**
     * Leaves impersonation and goes back to the dedicated admin's console. The admin stays
     * selected, so their organisation list comes back; the organisation and its form title are
     * cleared, the title last, as clearing the organisation emits a fresh "new organisation" title.
     */
    stopControlling(): void {
        this.adminDataSync.currentSelectedOrgId.next(undefined);
        this.adminDataSync.updateTitleSubject.next(undefined);
        this.store.dispatch(new UserAction.Update());
        this.store.dispatch([
            new OrganizationAction.Deactivate(),
            new QuestionnaireAction.Deactivate(),
            new QuestionnaireConfigAction.Deactivate(),
            new SolutionAction.Deactivate(),
        ]);
        this.storageService.removeItem(LocalStorageKeys.sudoMod);
        this.router.navigateByUrl('/dedicated_admin/home');
    }

    private buildNavigation(pricingPlan: OrganizationPricingPlanDto | undefined): Navigation {
        const licence = pricingPlan?.licenseDoc;
        return buildNavigation({
            translate: (key) => this.translateService.instant(key),
            hasFeature: (features, forOrganization) => this.featureService.hasUiFeatures(features, forOrganization),
            isSudo: this.featureService.hasSudoFeature(),
            isOnboarded: this.featureService.isOnboarded(),
            licence: {
                reduce: !!licence?.reduce,
                solutions: !!licence?.solutions,
                csrd: !!licence?.csrd,
                expert: !!licence?.expert,
            },
            openMeasure: () => this.openMeasure(),
            openHelp: () => this.openHelp(),
            resetAdminSelection: () => this.adminDataSync.resetSelection(),
        });
    }

    /**
     * Back to the scan the user opened from the matrix, or else to the matrix itself. The
     * scan activated in the background at login does not count: it leaves `cellSelected` false.
     */
    private openMeasure(): void {
        const questionnaire = this.store.selectSnapshot(QuestionnaireState.getQuestionnaire);
        const cellSelected = this.store.selectSnapshot(QuestionnaireState.hasCellBeenSelected);
        if (questionnaire && cellSelected) {
            this.router.navigate([questionnaire.typeId === 2 ? '/app/advanced_measure' : '/app/measure']);
        } else {
            this.router.navigate(['/app/measure-summary']);
        }
    }

    private openHelp(): void {
        this.dialogService.open(NeedHelpModalComponent, {
            modal: true,
            dismissableMask: true,
            autoZIndex: true,
            closable: true,
            closeOnEscape: true,
            showHeader: true,
            draggable: false,
            transitionOptions: '300ms cubic-bezier(0, 0, 0.2, 1)',
            styleClass: 'need-help-dialog dialog-xl',
        });
    }
}
